import { useMemo, useState } from "react";
import { RotateCcw, Sparkles } from "lucide-react";
import { Eyebrow, MaskLines, Reveal, useIsMobile } from "./motion";

const W = 600, H = 380, CELL = 20, MAX = 6;
const WALLS = [
  [20, 20, 580, 20, 0.1], [580, 20, 580, 360, 0.1], [580, 360, 20, 360, 0.1], [20, 360, 20, 20, 0.1],
  [200, 20, 200, 150, 0.16], [200, 190, 200, 360, 0.16], [20, 200, 170, 200, 0.16],
  [360, 20, 360, 120, 0.16], [360, 160, 360, 240, 0.32], [360, 280, 360, 360, 0.32], [200, 240, 360, 240, 0.16],
  [470, 20, 470, 120, 0.16],
];
const ROOMS = [
  { x: 110, y: 110, n: "RECEPTION" }, { x: 110, y: 285, n: "MEETING" }, { x: 280, y: 130, n: "OPEN OFFICE" },
  { x: 280, y: 305, n: "KITCHEN" }, { x: 415, y: 70, n: "OFFICE" }, { x: 525, y: 70, n: "COMMS" }, { x: 470, y: 250, n: "WAREHOUSE" },
];
const AUTO = [{ x: 105, y: 150 }, { x: 110, y: 250 }, { x: 285, y: 175 }, { x: 250, y: 335 }, { x: 500, y: 95 }, { x: 480, y: 300 }];

const cross = (a, b, c, d) => {
  const det = (b.x - a.x) * (d.y - c.y) - (b.y - a.y) * (d.x - c.x);
  if (!det) return false;
  const t = ((c.x - a.x) * (d.y - c.y) - (c.y - a.y) * (d.x - c.x)) / det;
  const u = ((c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x)) / det;
  return t > 0 && t < 1 && u > 0 && u < 1;
};
const signal = (p, ap) => {
  const loss = WALLS.reduce((s, w) => s + (cross(p, ap, { x: w[0], y: w[1] }, { x: w[2], y: w[3] }) ? w[4] : 0), 0);
  return 1 - Math.hypot(p.x - ap.x, p.y - ap.y) / 240 - loss;
};
const CELLS = [];
for (let y = 20; y < 360; y += CELL) for (let x = 20; x < 580; x += CELL) CELLS.push({ x: x + CELL / 2, y: y + CELL / 2 });
const tone = (s) => (s > 0.4 ? ["#10B981", 0.28] : s > 0.2 ? ["#F59E0B", 0.3] : ["#F43F5E", 0.35]);

export const WifiPlanner = () => {
  const [aps, setAps] = useState([{ x: 60, y: 60 }]);
  const mobile = useIsMobile();
  const grid = useMemo(() => CELLS.map((c) => (aps.length ? Math.max(...aps.map((a) => signal(c, a))) : -1)), [aps]);
  const good = grid.filter((s) => s > 0.4).length;
  const dead = grid.filter((s) => s <= 0.2).length;
  const coverage = Math.round((good / grid.length) * 100);

  const add = (e) => {
    if (aps.length >= MAX) return;
    const svg = e.currentTarget;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    if (p.x < 20 || p.x > 580 || p.y < 20 || p.y > 360) return;
    setAps((a) => [...a, { x: p.x, y: p.y }]);
  };
  const remove = (i) => (e) => {
    e.stopPropagation();
    setAps((a) => a.filter((_, j) => j !== i));
  };

  return (
    <section id="wifi-planner" className="relative py-24 md:py-32" data-testid="wifi-planner-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-4">
          <Eyebrow>Try it yourself</Eyebrow>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.02]">
            <MaskLines lines={["Kill the", <span className="font-serif-i font-normal text-cyan-300">dead zones.</span>]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 text-slate-400 leading-relaxed">Tap the floor plan to place access points and watch coverage fill in. Walls and the concrete warehouse wall weaken the signal — just like the real thing. Tap an access point to remove it.</p>
            <div className="mt-8 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
              <div className="bg-void p-3 sm:p-4"><p className="font-mono text-[9px] tracking-[0.2em] text-slate-500">COVERAGE</p><p className={`mt-1 font-display text-3xl font-bold ${coverage > 85 ? "text-emerald-400" : "text-white"}`} data-testid="wifi-coverage">{coverage}%</p></div>
              <div className="bg-void p-3 sm:p-4"><p className="font-mono text-[9px] tracking-[0.2em] text-slate-500">DEAD ZONES</p><p className="mt-1 font-display text-3xl font-bold text-rose-400" data-testid="wifi-dead">{Math.round((dead / grid.length) * 100)}%</p></div>
              <div className="bg-void p-3 sm:p-4"><p className="font-mono text-[9px] tracking-[0.2em] text-slate-500">APS</p><p className="mt-1 font-display text-3xl font-bold" data-testid="wifi-ap-count">{aps.length}/{MAX}</p></div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" onClick={() => setAps(AUTO)} data-testid="wifi-auto-place" className="inline-flex items-center gap-2 rounded-full bg-cyan-300 text-void px-5 py-2.5 text-sm font-semibold hover:bg-white transition-colors"><Sparkles size={15} /> Place like an engineer</button>
              <button type="button" onClick={() => setAps([{ x: 60, y: 60 }])} data-testid="wifi-reset" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-slate-300 hover:border-white/40 transition-colors"><RotateCcw size={15} /> Reset</button>
            </div>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-8 -mx-5 sm:mx-0" delay={0.1}>
          <div className="chamfer border-y sm:border border-white/10 bg-[#080B12] p-3 sm:p-4">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full cursor-crosshair" onClick={add} data-testid="wifi-floorplan">
              {CELLS.map((c, i) => {
                const [col, op] = tone(grid[i]);
                return <rect key={i} x={c.x - CELL / 2} y={c.y - CELL / 2} width={CELL} height={CELL} fill={col} opacity={op} style={{ transition: "fill 0.4s, opacity 0.4s" }} />;
              })}
              {WALLS.map((w, i) => <line key={i} x1={w[0]} y1={w[1]} x2={w[2]} y2={w[3]} stroke={w[4] > 0.2 ? "#94A3B8" : "#CBD5E1"} strokeWidth={w[4] > 0.2 ? 5 : 2.5} strokeLinecap="round" />)}
              {ROOMS.map((r) => <text key={r.n} x={r.x} y={r.y} textAnchor="middle" fill="rgba(226,232,240,0.55)" fontSize={mobile ? 13 : 9} fontFamily="JetBrains Mono, monospace" letterSpacing={mobile ? 0.5 : 1.5} pointerEvents="none">{r.n}</text>)}
              {aps.map((a, i) => (
                <g key={`${a.x}-${a.y}`} onClick={remove(i)} className="cursor-pointer" data-testid={`wifi-ap-${i}`}>
                  <circle cx={a.x} cy={a.y} r="26" fill="none" stroke="#3B82F6" className="wave-sm" />
                  <circle cx={a.x} cy={a.y} r="11" fill="#F1F5F9" stroke="#CBD5E1" />
                  <circle cx={a.x} cy={a.y} r="5" fill="none" stroke="#3B82F6" strokeWidth="1.6" />
                </g>
              ))}
            </svg>
            <div className="mt-3 flex flex-wrap items-center gap-4 font-mono text-[9px] tracking-[0.2em] text-slate-500">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-emerald-500/70" /> STRONG</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-amber-500/70" /> WEAK</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-rose-500/70" /> DEAD ZONE</span>
              <span className="sm:ml-auto">THICK WALL = CONCRETE</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
